<!-- eslint-disable max-len -->
<template>
  <div class="p-t-15 p-b-15">
    <BaseCard v-if="isEnterpriseAccount">
      <template #card-header>
        <h2>{{ $t('Budget tracker') }}</h2>
      </template>
      <div>
        <div>
          <EditSettingsOption
            id="input-project-tracking"
            v-model="userState.projectTracking"
            :label="$t('Enable project tracking')"
            :help-info="
              $t(
                'Enables project tracking so jobs can be associated with hiring projects and real spend and projected spend can be tracked across jobs and projects',
              )
            "
            :loading="latestClickedItemKey === 'project_tracking' && saving"
            :failed="latestClickedItemKey === 'project_tracking' && savingFailed"
            @update:model-value="save('project_tracking', $event)"
          />
        </div>
        <div>
          <EditSettingsOption
            id="input-required-job-budget"
            v-model="userState.requiredJobBudget"
            :label="$t('Require internal budgets on jobs and hires')"
            :help-info="$t('Job budget tracking will be required on all jobs and hires')"
            :loading="latestClickedItemKey === 'required_job_budget' && saving"
            :failed="latestClickedItemKey === 'required_job_budget' && savingFailed"
            @update:model-value="save('required_job_budget', $event)"
          />
        </div>
      </div>
    </BaseCard>

    <FeatureFlagged name="msp-mvp">
      <BaseCard>
        <template #card-header>
          <h2>{{ $t('Job postings') }}</h2>
        </template>
        <div>
          <div>
            <EditSettingsOption
              id="input-required-job-rate"
              v-model="userState.requiredJobRate"
              :label="$t('Require pay rate on job posts')"
              :help-info="
                $t(
                  'A pay rate type and amount (or a rate range) will be required on every job before it can be published. Drafts can still be saved without a rate.',
                )
              "
              :loading="latestClickedItemKey === 'required_job_rate' && saving"
              :failed="latestClickedItemKey === 'required_job_rate' && savingFailed"
              @update:model-value="save('required_job_rate', $event)"
            />
          </div>
          <div>
            <EditSettingsOption
              id="toggle-default-sourcing-deadline"
              v-model="defaultSourcingDeadlineEnabled"
              :label="$t('Set a default candidate sourcing deadline')"
              :help-info="
                $t(
                  'New job posts will default their sourcing deadline to a set number of hours after being published.',
                )
              "
              @update:model-value="onToggleDefaultSourcingDeadline"
            />
            <div v-if="defaultSourcingDeadlineEnabled" class="m-t-10 m-l-20">
              <label for="input-default-sourcing-deadline-hours">
                {{ $t('Hours after publishing') }}
              </label>
              <WdsInput
                id="input-default-sourcing-deadline-hours"
                v-model="userState.defaultSourcingDeadlineHours"
                type="number"
                data-test="input-default-sourcing-deadline-hours"
                @blur="
                  save(
                    'default_sourcing_deadline_hours',
                    normalizeDeadlineHours(userState.defaultSourcingDeadlineHours),
                  )
                "
              />
            </div>
          </div>
        </div>
      </BaseCard>
    </FeatureFlagged>

    <BaseCard>
      <template #card-header>
        <h2>{{ $t('Hires') }}</h2>
      </template>
      <div>
        <div>
          <EditSettingsOption
            v-model="userState.requireContractDateStart"
            :label="$t('Require to set a start date on all contracts')"
            :help-info="$t('A start date will be required on any new contracts')"
            :loading="latestClickedItemKey === 'require_contract_date_start' && saving"
            :failed="latestClickedItemKey === 'require_contract_date_start' && savingFailed"
            @update:model-value="save('require_contract_date_start', $event)"
          />
        </div>
        <div>
          <EditSettingsOption
            v-model="userState.requireContractDateEnd"
            :label="$t('Require to set a end date on all contracts')"
            :help-info="$t('A end date will be required on any new contracts')"
            :loading="latestClickedItemKey === 'require_contract_date_end' && saving"
            :failed="latestClickedItemKey === 'require_contract_date_end' && savingFailed"
            @update:model-value="save('require_contract_date_end', $event)"
          />
        </div>
        <div>
          <EditSettingsOption
            v-model="userState.poNumberMandatory"
            :label="$t('Require PO number on contracts')"
            :help-info="$t('A Purchase Order number will be required on all hires')"
            :loading="latestClickedItemKey === 'po_number_mandatory' && saving"
            :failed="latestClickedItemKey === 'po_number_mandatory' && savingFailed"
            @update:model-value="save('po_number_mandatory', $event)"
          />
        </div>
        <div>
          <EditSettingsOption
            v-model="userState.enableMultiCurrencyContracts"
            :label="$t('Enable multi-currency contracts')"
            :help-info="
              $t(
                'This will allow your team to create and offer contracts to your trusted contacts in their preferred currency',
              )
            "
            :loading="latestClickedItemKey === 'disable_multi_currency_contracts' && saving"
            :failed="latestClickedItemKey === 'disable_multi_currency_contracts' && savingFailed"
            @update:model-value="save('disable_multi_currency_contracts', !$event)"
          />
        </div>
        <div>
          <EditSettingsOption
            v-model="userState.includeCancelledContractsInExports"
            :label="$t('Include cancelled contracts in data exports')"
            :help-info="$t('Cancelled contracts are not included in data exports by default')"
            :loading="latestClickedItemKey === 'include_cancelled_contracts_in_exports' && saving"
            :failed="
              latestClickedItemKey === 'include_cancelled_contracts_in_exports' && savingFailed
            "
            @update:model-value="save('include_cancelled_contracts_in_exports', $event)"
          />
        </div>
      </div>
    </BaseCard>

    <BaseCard>
      <template #card-header>
        <h2>{{ $t('Payment requests') }}</h2>
      </template>
      <div>
        <div>
          <EditSettingsOption
            v-model="userState.poNumberBillMandatory"
            :label="$t('Require PO number on bills')"
            :help-info="$t('A Purchase Order number will be required on all bills before approval')"
            :loading="latestClickedItemKey === 'po_number_bill_mandatory' && saving"
            :failed="latestClickedItemKey === 'po_number_bill_mandatory' && savingFailed"
            @update:model-value="save('po_number_bill_mandatory', $event)"
          />
        </div>

        <div>
          <EditSettingsOption
            v-model="userState.requireTimesheetOnBills"
            :label="$t('Require timesheets on payment requests')"
            :help-info="$t('This only applies to hires with time-based rates (e.g. hourly or daily).')"
            :loading="latestClickedItemKey === 'require_timesheet_on_bills' && saving"
            :failed="latestClickedItemKey === 'require_timesheet_on_bills' && savingFailed"
            @update:model-value="save('require_timesheet_on_bills', $event)"
          />
        </div>

        <!-- Only relevant when timesheets are required: it has no effect otherwise. -->
        <transition name="expand-setting">
          <div v-if="userState.requireTimesheetOnBills">
            <EditSettingsOption
              v-model="userState.allowFixedAmountPaymentRequests"
              :label="$t('Allow workers to submit fixed amount payment requests')"
              :help-info="
                $t(
                  'Lets workers submit a one-off, fixed-amount payment request instead of a timesheet. Useful for lump-sum charges.',
                )
              "
              :loading="latestClickedItemKey === 'allow_fixed_amount_payment_requests' && saving"
              :failed="latestClickedItemKey === 'allow_fixed_amount_payment_requests' && savingFailed"
              @update:model-value="save('allow_fixed_amount_payment_requests', $event)"
            />
          </div>
        </transition>

        <!-- This was set to false originally - LJ  -->
        <div v-if="false">
          <EditSettingsOption
            v-model="userState.dualApprovalBills"
            :label="$t('Dual approval on bill timesheets')"
            :help-info="
              $t(
                'approval of timesheet on a bill requires approval by two team members prior to payment.',
              )
            "
            :loading="latestClickedItemKey === 'dual_approval_bills' && saving"
            :failed="latestClickedItemKey === 'dual_approval_bills' && savingFailed"
            @update:model-value="save('dual_approval_bills', $event)"
          />
        </div>

        <div>
          <EditSettingsOption
            v-model="userState.expenseReportsEnabled"
            :label="$t('Enable expense reports')"
            :help-info="
              $t(
                'Allow a freelancer to submit expense reports on a bill. They will need to upload proof of each expense submitted.',
              )
            "
            :loading="latestClickedItemKey === 'expense_reports_enabled' && saving"
            :failed="latestClickedItemKey === 'expense_reports_enabled' && savingFailed"
            @update:model-value="save('expense_reports_enabled', $event)"
          />
        </div>
      </div>
    </BaseCard>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { WdsInput } from '@worksome/wds-components'
import BaseCard from '@/components/BaseCard.vue'
import enums from '@/utils/enum'
import EditSettingsOption from '@/components/EditSettingsOption.vue'
import FeatureFlagged from '@/components/FeatureFlagged.vue'

export default {
  components: {
    BaseCard,
    EditSettingsOption,
    FeatureFlagged,
    WdsInput,
  },
  props: {
    isEnterpriseAccount: { type: Boolean, default: false },
    projectTracking: { type: Boolean, default: false },
    requiredJobBudget: { type: Boolean, default: false },
    requiredJobRate: { type: Boolean, default: false },
    defaultSourcingDeadlineHours: { type: Number, default: null },
    includeCancelledContractsInExports: { type: Boolean, default: false },
    requireContractDateStart: { type: Boolean, default: false },
    requireContractDateEnd: { type: Boolean, default: false },
    poNumberMandatory: { type: Boolean, default: false },
    dualApprovalBills: { type: Boolean, default: false },
    poNumberBillMandatory: { type: Boolean, default: false },
    requirePeriodOnBills: { type: Boolean, default: false },
    requireTimesheetOnBills: { type: Boolean, default: false },
    allowFixedAmountPaymentRequests: { type: Boolean, default: false },
    expenseReportsEnabled: { type: Boolean, default: false },
    enableMultiCurrencyContracts: { type: Boolean, default: false },
  },
  data() {
    return {
      userHasInteractedWithContents: false,
      userState: this.initialUserState(),
      defaultSourcingDeadlineEnabled: this.defaultSourcingDeadlineHours != null,
      latestClickedItemKey: null,
      saving: false,
      savingFailed: false,
    }
  },
  methods: {
    initialUserState() {
      return {
        projectTracking: this.projectTracking,
        requiredJobBudget: this.requiredJobBudget,
        requiredJobRate: this.requiredJobRate,
        defaultSourcingDeadlineHours: this.defaultSourcingDeadlineHours,
        enableMultiCurrencyContracts: this.enableMultiCurrencyContracts,
        includeCancelledContractsInExports: this.includeCancelledContractsInExports,
        requireContractDateStart: this.requireContractDateStart,
        requireContractDateEnd: this.requireContractDateEnd,
        poNumberMandatory: this.poNumberMandatory,
        poNumberBillMandatory: this.poNumberBillMandatory,
        dualApprovalBills: this.dualApprovalBills,
        requirePeriodOnBills: this.requirePeriodOnBills,
        requireTimesheetOnBills: this.requireTimesheetOnBills,
        allowFixedAmountPaymentRequests: this.allowFixedAmountPaymentRequests,
        expenseReportsEnabled: this.expenseReportsEnabled,
      }
    },
    normalizeDeadlineHours(value) {
      return [null, ''].includes(value) ? null : Number(value)
    },
    onToggleDefaultSourcingDeadline(enabled) {
      this.defaultSourcingDeadlineEnabled = enabled

      if (!enabled) {
        this.userState.defaultSourcingDeadlineHours = null
        this.save('default_sourcing_deadline_hours', null)
      }
    },
    async save(key, value) {
      this.latestClickedItemKey = key
      this.savingFailed = false
      this.saving = true

      try {
        await this.$apollo.mutate({
          mutation: gql`mutation (
                $globalId: ID!,
              ) {
              updateCompany(input: {
                  globalId: $globalId,
                  settings: {
                        ${key}: ${value}
                  }
              }) {
                globalId
                settings {
                    ${key}
                }
              }
            }`,
          variables: {
            globalId: this.account.globalId,
          },
        })

        this.saving = false
      } catch (e) {
        this.saving = false
        this.savingFailed = true
        this.saveFailedActions()
      }
    },
    saveFailedActions() {
      this.$store.commit('sendNotification', {
        type: enums.notification.types.ERROR,
        sender: this,
        severity: enums.notification.severity.CRITICAL,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.network-loader {
  margin-top: -7px;
}

.expand-setting-enter-active,
.expand-setting-leave-active {
  transition: max-height 0.2s ease, opacity 0.2s ease;
  overflow: hidden;
  max-height: 80px;
}

.expand-setting-enter-from,
.expand-setting-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
